// STAI2 Video Call Service
import { Repository } from '../../../servics/httpsServics';
import axios from 'axios';

// Get waiting users count for STAI2 (project_id = 2) - CORRECTED ENDPOINT
export const getWaitingUserCount = (projectId) => {
  return Repository.get(`call/waiting-count-by-project/${projectId}`)
    .catch((error) => {
      if (error.response && error.response.status === 404) {
        // Fallback to alternative endpoint
        return Repository.get(`call/waiting-count/${projectId}`);
      }
      throw error;
    });
};

// Get next waiting user for STAI2
export const getWaitingUser = async (projectId) => {
  try {
    return await Repository.get(`/call/waiting-user-by-project/${projectId}`, {});
  } catch (error) {
    // If 404, return empty result gracefully
    if (error.response && error.response.status === 404) {
      console.log('⚠️ STAI2 - No waiting users found (404)');
      return { data: null };
    }
    throw error;
  }
};

// Initialize STAI2 call for registered users
export const initializeSTAI2Call = (callData) => {
  return Repository.post('/stai2/calls/initialize', callData);
};

// Initialize STAI2 call for guest users
export const initializeSTAI2GuestCall = (callData) => {
  return Repository.post('/stai2/calls/initialize-guest', callData);
};

// End STAI2 call
export const endSTAI2Call = (endData) => {
  return Repository.post('/stai2/calls/end', endData);
};

// Get queue status for guest (no auth required)
export const getQueueStatus = (sessionToken) => {
  return Repository.get(`/stai2/queue-status/${sessionToken}`, {});
};

// Leave queue (no auth required)
export const leaveQueue = (sessionToken) => {
  return Repository.post('/stai2/leave-queue', { sessionToken });
};

// Join guest queue (no auth required)
export const guestAuth = (guestData) => {
  return Repository.post('/stai2/guest-auth', guestData);
};

// Get STAI2 projects
export const getSTAI2Projects = () => {
  return Repository.get('/stai2/projects');
};

// Set reason for call
export const setCallReason = (reasonData) => {
  return Repository.post('/stai2/calls/reason', reasonData);
};

// Get waiting users (new endpoint confirmed by backend)
export const getWaitingUsers = (projectId = 2) => {
  return Repository.get(`/stai2/calls/get-waiting-users?projectId=${projectId}`);
};

// Check if interpreters are available (no auth required for guests)
export const checkInterpretersAvailable = async (sessionToken, projectId) => {    // Use existing endpoint that returns both user_count and interpreter_count
    const response = await Repository.get(
      `/stai2/queue-status?sessionToken=${sessionToken}&projectId=${projectId}`
    );

    return {
      count: response.data?.interpreterCount || 0,
      queuePosition: response.data?.queuePosition || null
    };
};

// Save call rating
export const saveRating = (ratingData) => {
  return Repository.post('/stai2/save-rating', ratingData);
};

// Get STAI2 projects with allowed locations (dynamic from backend)
export const getSTAI2ProjectsWithLocations = async () => {
  try {
    const response = await Repository.get('/stai2/projects');
    return response.data || response; // Handle different response structures
  } catch (error) {
    console.warn('⚠️ STAI2 - Error getting projects with locations:', error);
    // Fallback: return basic STAI2 project without location restrictions
    return [
      {
        id: 2,
        name: 'STAI2',
        code: 'STAI2',
        description: 'Servizio interpretariato STAI2',
        requires_registration: false,
        allows_physical_appointments: false,
        locations_allowed: null // null = all provinces allowed
      }
    ];
  }
};

// Check if a province is allowed for STAI2 service
export const isProvinceAllowedForSTAI2 = async (userProvince) => {
  try {
    console.log('🔍 STAI2 - Starting province check for:', userProvince);

    const projects = await getSTAI2ProjectsWithLocations();
    console.log('📡 STAI2 - Raw projects response:', projects);

    const stai2Project = projects.find((p) => p.code === 'STAI2');
    console.log('🎯 STAI2 - Found STAI2 project:', stai2Project);

    if (!stai2Project) {
      console.error('❌ STAI2 - No STAI2 project found in response!');
      return {
        allowed: false,
        reason: 'no_project_found'
      };
    }

    // Check if project is active
    if (stai2Project.is_active === false) {
      console.log('🚫 STAI2 - Project is inactive');
      return {
        allowed: false,
        reason: 'project_inactive'
      };
    }

    const locationsAllowed = stai2Project.locations_allowed;
    console.log('📍 STAI2 - locations_allowed field:', locationsAllowed);

    // If locations_allowed is null or empty = all provinces are allowed
    if (!locationsAllowed || (Array.isArray(locationsAllowed) && locationsAllowed.length === 0)) {
      console.log('🌍 STAI2 - All provinces allowed (no restrictions)');
      return {
        allowed: true,
        reason: 'no_restrictions'
      };
    }

    // Check if user province is in allowed list (expecting IT-XX format)
    const allowedCodes = locationsAllowed.map((loc) => loc.code);
    console.log('📋 STAI2 - Allowed province codes:', allowedCodes);
    console.log('🔍 STAI2 - User province to check:', userProvince);

    const isAllowed = allowedCodes.includes(userProvince);

    console.log('✅ STAI2 - Province check result:', {
      userProvince,
      allowedProvinces: allowedCodes,
      isAllowed
    });

    return {
      allowed: isAllowed,
      reason: isAllowed ? 'province_allowed' : 'province_not_allowed',
      allowedProvinces: stai2Project.locations_allowed
    };
  } catch (error) {
    console.error('❌ STAI2 - Error checking province:', error);
    // Fallback: deny access if API fails (security)
    return {
      allowed: false,
      reason: 'api_error',
      error: error.message
    };
  }
};

// ✅ STAI2 Room Management APIs (IMPLEMENTED)
export const createSTAI2Room = async (roomData) => {
  try {
    console.log('🏗️ STAI2 - Creating room with data:', roomData);
    const response = await Repository.post('/stai2/calls/create-room', roomData);
    console.log('✅ STAI2 - Room created:', response.data);
    return response;
  } catch (error) {
    console.error('❌ STAI2 - Failed to create room:', error);
    throw error;
  }
};

export const getSTAI2RoomByPeer = async (peer_id) => {
  try {
    console.log('🔍 STAI2 - Getting room for peer:', peer_id);
    const response = await Repository.get(`/stai2/calls/room-by-peer?peer_id=${peer_id}`);
    console.log('✅ STAI2 - Room found:', response.data);
    return response;
  } catch (error) {
    console.error('❌ STAI2 - Failed to get room by peer:', error);
    //throw error;
  }
};

// ✅ STAI2 Chat APIs - BACKEND FIXED CORS + MESSAGE FORMAT
export const getSTAI2ChatMessages = async (room_id, sessionToken = null, auth = null) => {
  try {
    console.log('💬 STAI2 - Getting chat messages for room:', room_id);

    const headers = {
      'Content-Type': 'application/json'
    };

    // Backend now accepts sessiontoken header correctly (CORS fixed)
    if (sessionToken) {
      headers.sessiontoken = sessionToken; // Use lowercase for better CORS compatibility
    }

    if (auth?.authToken) {
      headers.Authorization = `Bearer ${auth.authToken}`;
    }

    const response = await Repository.get(`/stai2/chat/messages?room_id=${room_id}`, {
      headers
    });

    console.log('✅ STAI2 - Chat messages loaded:', response.data?.length || 0);
    return response;
  } catch (error) {
    console.error('❌ STAI2 - Failed to get chat messages:', error);
    throw error;
  }
};

export const sendSTAI2ChatMessage = async (messageData, sessionToken = null, auth = null) => {
  try {
    console.log('📤 STAI2 - Sending chat message:', messageData);

    const headers = { 'Content-Type': 'application/json' };

    // For guest users, add sessiontoken header
    if (sessionToken) {
      headers.sessiontoken = sessionToken; // Use lowercase for better CORS compatibility
    }

    if (auth?.authToken) {
      headers.Authorization = `Bearer ${auth.authToken}`;
    }

    // Per guest, sessionToken è già nel messageData.session_token
    const response = await Repository.post('/stai2/chat/send-message', messageData, {
      headers
    });

    console.log('✅ STAI2 - Chat message sent successfully');
    return response;
  } catch (error) {
    console.error('❌ STAI2 - Failed to send chat message:', error);
    throw error;
  }
};

// 🎨 Whiteboard functionality uses real-time multiplayer sync only (no API saving needed)

// 🔧 Debug utilities for STAI2 system
const debugSTAI2System = {
  logSystemInfo() {
    console.log('🎯 STAI2 System Info:');
    console.log('📍 Backend URL:', Repository.defaults.baseURL);
    console.log('🎨 Whiteboard URL: https://csblombardia.ens.it:5858');
    console.log('📱 Project ID: 2 (STAI2)');
    return { status: 'ready for testing' };
  },

  // Log room information
  logRoomInfo: function() {
    console.log('🏗️ STAI2 Room System:');
    console.log('- Rooms are created automatically when videocall starts');
    console.log('- Backend handles duplicate prevention');
    console.log('- Chat/whiteboard tied to room_id');
    console.log('- API: /stai2/calls/room-by-peer?peer_id=xxx');
  }
};

// Make debug utilities available in browser console
if (typeof window !== 'undefined') {
  window.debugSTAI2 = debugSTAI2System;
  console.log('🔧 STAI2 Debug utilities available at: window.debugSTAI2');
  console.log('📋 System info: window.debugSTAI2.logSystemInfo()');
}
